import { pickRandomItems } from "./array-util";

/**
 * Parses list items in text.
 * If the lines is a list item, parse them and clean them. Otherwise just return the lines.
 * @param text
 */
export function parseListItemsInText(text: string): string[] | undefined {
	// Split the text into lines and clean the lines
	const lines = unmarkdownify(text.trim())
		.split(/\n/)
		.map((line) => line.trim());

	// For every list item line, clean the line
	const listItemLines = lines.filter(isListItem).map((line) =>
		unquotify(
			line
				// Remove until first letter
				.replace(/^([\d\s-]+\.|-(?=[A-Z])|[-*]\s)/i, "")
				// Replace ": with :
				.replace(/["']:/g, ":")
				.trim()
		)
	);

	if (listItemLines.length === 0) return lines.filter(hasValue);

	return listItemLines;
}

export function isListItem(text: string): boolean {
	return text.length > 0 && text.match(/^\s?[0-9.\-*•]/) != null;
}

export function parseSectionsInText(text: string): Record<string, string> | undefined {
	text = text.trim();

	const sections: Record<string, string> = {};

	/* First, try parse this case (sections without multilines)
	# Title 1: Idea 1, Idea 2
  # Title 2: Idea 1, Idea 2

	 */

	const lines = text.split("\n").map((line) => line.trim());

	if (!lines.some((line) => !line.startsWith("# "))) {
		// All lines start with "# "
		for (const line of lines) {
			// Split ":"
			const parts = line.split(":").map((line) => unquotify(line.trim())) as [string, string];
			if (parts.length !== 2) return undefined;
			sections[parts[0].replace("# ", "")] = parts[1];
		}
	} else {
		/* Second, try parse this case (sections with multilines)
		# Title 1
		- Idea 1
		- Idea 2
		# Title 2
		- Idea 1
		- Idea 2
		 */

		// TODO: Den her skal ikke bare tage et # hvor som helst men starte med linjen ^\s? (m modifier)
		const regex = /(#\s*[*\s]*(?<title1>.+?)[*\s:]*)\n(?<content>[\s\S]+?)(?=(\n#)|\*\*|$)/g;
		//const regex = /(#\s*(?<title1>.+?):?|\*\*(?<title2>.+?)\*\*)\n(?<content>[\s\S]+?)(?=(\n#)|\*\*|$)/g;

		const matches = [...text.matchAll(regex)];

		if (matches.length === 0) return undefined;

		for (const match of matches) {
			sections[match.groups?.title1 ?? match.groups?.title2 ?? ""] = match.groups?.content ?? "";
		}
	}

	return sections;
}

export function parseSectionsWithListItemsInText(text: string): Record<string, string[]> | undefined {
	const sections = parseSectionsInText(text);

	if (sections == null) return undefined;

	const sectionsWithListItems: Record<string, string[]> = {};
	for (const [title, content] of Object.entries(sections)) {
		sectionsWithListItems[title] = parseListItemsInText(content) || [content.trim()];
	}

	return sectionsWithListItems;
}

/**
 * Extracts a JSON object from a string.
 * @param text
 */
export function parseJSONInText(text: string) {
	// Find the start and end indexes of the JSON string
	const startIndex = text.indexOf("{");
	const endIndex = text.lastIndexOf("}") + 1;

	// Extract the JSON string from the text
	let jsonString = text.substring(startIndex, endIndex);

	// Parse the JSON string and return the resulting object
	let json: any;
	try {
		json = JSON.parse(jsonString);
	} catch (e) {
		console.error(e);

		// Fix potential errors in  the JSON so it can parse
		jsonString = jsonString.replace(/(\w+):/g, '"$1":');

		json = JSON.parse(jsonString);
	}

	// Convert all values in the JSON to arrays
	for (const key in json) {
		json[key] = Array.isArray(json[key]) ? json[key] : [json[key]];
	}

	return json;
}

export function quotify(text: string): string {
	return `"${text}"`;
}

export function unquotify(text: string): string {
	const pairs = [`"`, `'`, "`"];

	for (const pair of pairs) {
		if (text.startsWith(pair)) {
			text = text.substring(1);
		}

		if (text.endsWith(pair)) {
			text = text.substring(0, text.length - 1);
		}

		// const regex = new RegExp(`^\\s*${pair}(.*)${pair}\\s*$`);
		//
		// if (regex.test(text)) return text.replace(regex, "$1");
	}

	// Remove quote if there is only one quote, and the text starts with it
	// if (text.startsWith(`"`) && text.lastIndexOf(`"`) === 0) {
	// 	return text.replace(/^"(.*)"$/, "$1");
	// }

	return text;
}

export function unmarkdownify(text: string): string {
	// Remove:
	// - "**"
	// - "__"
	return text.replace(/(\*\*|__)/g, "");
}

/**
 * Turns a string into kebab case.
 * @param str
 */
export function pascalToKebabCase(str: string): string {
	return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * Turns string parts into a numbered list.
 * @param parts
 */
export function listifyText(parts: string[]): string {
	return parts.map((part, index) => `${index + 1}. ${part}`).join("\n");
}

/**
 * Adds commands between parts and ends with "and" before last item if there are more than one.
 * @param parts
 * @param lastSeparator
 */
export function commaListifyText(parts: string[], lastSeparator = "and"): string {
	const len = parts.length;

	if (len > 1) {
		const allButLast = parts.slice(0, -1).join(", ");
		return `${allButLast} ${lastSeparator} ${parts[len - 1]}`;
	}

	return parts.join(", ");
}

/**
 * Uppercases the first letter of a string.
 * @param str
 */
export function uppercaseFirstLetter(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Returns a random character.
 */
export function randomCharacter() {
	return String.fromCharCode(Math.floor(Math.random() * 26) + 97);
}

/**
 * Determines if a string is empty.
 * @param text
 */
export function hasValue(text: string | undefined | null): text is string {
	return text != null && text.trim().length > 0;
}

/**
 * Sanitizes HTML.
 * @param str
 */
export function sanitizeHTML(str: string) {
	return str.replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Highlights a text by adding <mark> element around.
 * @param text
 * @param query
 * @param isMarkdown
 */
export function highlightText({ text, query, isHTML }: { text?: string; query?: string; isHTML?: boolean }): string {
	if (isHTML) {
		text = sanitizeHTML(text ?? "");
	}

	if (text == null || text.length === 0 || query == null || query.length === 0) return text ?? "";
	if (query == null || query.length === 0) return text;
	query = removeSpecialCharacters(query);
	return text.replaceAll(
		new RegExp(
			`(${query
				?.split(" ")
				.filter((t) => t.length > 0)
				.join("|")})`,
			"gi"
		),
		(match: string) => (isHTML ? `<mark>${match}</mark>` : `==${match}==`)
	);
}

/**
 * Removes regex characters and markdown characters from text.
 * @param text
 */
export function removeSpecialCharacters(text: string) {
	return text.replaceAll(/([.*+?^=!:${}()|[\]/\\]<>#*_-`)/gi, "\\$1");
}

/**
 * Truncates a string to a given length from the middle.
 * @param str
 * @param maxLength
 * @param separator
 */
export function truncateString(str: string, maxLength: number, separator = "..."): string {
	if (str.length <= maxLength) {
		return str;
	}

	// Prioritize the last part of the string
	const firstPart = str.substring(0, maxLength / 3).split(" ");
	const lastPart = str.substring(str.length - maxLength / 1.5).split(" ");

	return [
		...(firstPart.length > 1 ? firstPart.slice(0, firstPart.length - 1) : firstPart),
		separator,
		...(lastPart.length > 1 ? lastPart.slice(1) : lastPart)
	].join(" ");
}

/**
 * Truncates AI user input.
 * @param text
 * @param length
 */
export function truncateAIUserInput(text?: string, length: "short" | "medium" | "long" = "medium") {
	if (text == null) return undefined;
	switch (length) {
		case "short":
			return truncateString(text, 500);
		case "medium":
			return truncateString(text, 1000);
		case "long":
			return truncateString(text, 2000);
	}
}

/**
 * Counts the number of matches in a string.
 * @param text
 * @param needle
 */
export function countMatches(text: string, needle: string) {
	return text.split(needle).length - 1;
}

/**
 * Joins text parts with a separator and filters out the empty ones.
 * @param parts
 * @param separator
 */
export function joinText(parts: (string | undefined | null)[], separator = ". ") {
	return parts.filter(hasValue).join(separator);
}

/**
 * Returns a random letter.
 * @param count
 * @param alphabet
 */
export function getRandomLetters(count: number, alphabet: string = "abcdefghijklmnopqrstuvwxyz"): string[] {
	return pickRandomItems(alphabet.split(""), count);
}
